// import React from "react";
// import { useLocation } from "react-router-dom";
// import ErrorBoundary from "./ErrorBoundary";

// class PageErrorBoundaryCore extends React.Component {
//   constructor(props) {
//     super(props);

//     this.state = {
//       hasError: false,
//     };
//   }

//   static getDerivedStateFromError() {
//     return {
//       hasError: true,
//     };
//   }

//   componentDidCatch(error, errorInfo) {
//     const pageName = this.props.pageName || "Unknown Page";

//     console.groupCollapsed(
//       `%c[NexGen Page Error] ${pageName}`,
//       "color:#e5484d;font-weight:bold;",
//     );

//     console.error("Page:", pageName);
//     console.error("Error:", error);
//     console.error("Message:", error?.message || "Unknown error");
//     console.error("Component stack:", errorInfo?.componentStack);
//     console.error("Current URL:", window.location.href);
//     console.error("Captured at:", new Date().toISOString());

//     console.groupEnd();
//   }

//   componentDidUpdate(previousProps) {
//     if (previousProps.resetKey !== this.props.resetKey && this.state.hasError) {
//       this.setState({
//         hasError: false,
//       });
//     }
//   }

//   retryPage = () => {
//     this.setState({
//       hasError: false,
//     });
//   };

//   render() {
//     if (this.state.hasError) {
//       if (this.props.silent) {
//         return null;
//       }

//       return <ErrorBoundary />;
//     }

//     return this.props.children;
//   }
// }

// const PageErrorBoundary = ({ children, pageName, silent = false }) => {
//   const location = useLocation();

//   return (
//     <PageErrorBoundaryCore
//       pageName={pageName}
//       silent={silent}
//       resetKey={`${location.pathname}${location.search}`}
//     >
//       {children}
//     </PageErrorBoundaryCore>
//   );
// };

// export default PageErrorBoundary;

import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

import LoadingAnimation from "../Components/Loading Animations/LoadingAnimations";

class PageErrorBoundaryCore extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      hasError: false,
      error: null,
      retrying: false,
      retryCount: 0,
    };

    this.retryTimer = null;
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error, errorInfo) {
    console.groupCollapsed(
      `%c[NexGen Page Error] ${this.props.pageName}`,
      "color:#e5484d;font-weight:700;",
    );

    console.error("Page:", this.props.pageName);
    console.error("Error:", error);
    console.error("Component stack:", errorInfo?.componentStack);
    console.error("URL:", window.location.href);
    console.error("Captured:", new Date().toISOString());

    console.groupEnd();

    /*
     * Retry only once.
     *
     * This can recover from temporary module/context timing problems,
     * but it avoids repeatedly remounting a permanently broken page.
     */
    if (this.state.retryCount < 1) {
      this.setState({
        retrying: true,
        retryCount: this.state.retryCount + 1,
      });

      this.retryTimer = window.setTimeout(() => {
        this.setState({
          hasError: false,
          error: null,
          retrying: false,
        });
      }, 1200);
    }
  }

  componentDidUpdate(previousProps) {
    if (previousProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.resetBoundary();
    }
  }

  componentWillUnmount() {
    if (this.retryTimer) {
      window.clearTimeout(this.retryTimer);
    }
  }

  resetBoundary = () => {
    if (this.retryTimer) {
      window.clearTimeout(this.retryTimer);
    }

    this.setState({
      hasError: false,
      error: null,
      retrying: false,
      retryCount: 0,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleHome = () => {
    this.resetBoundary();
    this.props.navigateHome();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.state.retrying) {
      return <LoadingAnimation />;
    }

    if (this.props.silent) {
      return null;
    }

    return (
      <main className="nexgen-page-error">
        <section className="nexgen-page-error__card">
          <div className="nexgen-page-error__icon">
            <i className="fa-solid fa-triangle-exclamation" />
          </div>

          <span className="nexgen-page-error__eyebrow">
            Temporary page problem
          </span>

          <h1>This page could not load correctly.</h1>

          <p>
            The navigation and other website features are still available. You
            can retry this page, reload the website, or return to the home page.
          </p>

          {process.env.NODE_ENV === "development" && this.state.error && (
            <details className="nexgen-page-error__details">
              <summary>Development details</summary>

              <pre>
                {this.state.error?.stack ||
                  this.state.error?.message ||
                  String(this.state.error)}
              </pre>
            </details>
          )}

          <div className="nexgen-page-error__actions">
            <button
              type="button"
              className="nexgen-page-error__primary"
              onClick={this.resetBoundary}
            >
              <i className="fa-solid fa-rotate-right" />
              Try Again
            </button>

            <button type="button" onClick={this.handleReload}>
              Reload Website
            </button>

            <button type="button" onClick={this.handleHome}>
              Go to Home
            </button>
          </div>
        </section>
      </main>
    );
  }
}

const PageErrorBoundary = ({
  children,
  pageName = "Website Page",
  silent = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <PageErrorBoundaryCore
      pageName={pageName}
      silent={silent}
      resetKey={`${location.pathname}${location.search}`}
      navigateHome={() =>
        navigate("/", {
          replace: true,
        })
      }
    >
      {children}
    </PageErrorBoundaryCore>
  );
};

export default PageErrorBoundary;
